import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLanguage } from './LanguageContext';
import { getTranslation } from './translations';
import LanguageSelector from './LanguageSelector';

// BackButton component
const BackButton = ({ tournamentId }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const handleBackClick = () => {
    // Navigate to the tournament list page instead of browser history
    navigate('/mecz/turniej-rankingowy/dodaj');
  };
  
  return (
    <button
      onClick={handleBackClick}
      style={{
        background: 'none',
        border: 'none',
        fontSize: 24,
        cursor: 'pointer',
        color: '#888',
        padding: 8,
        borderRadius: 4,
        transition: 'color 0.2s',
        textDecoration: 'none'
      }}
      onMouseOver={(e) => e.target.style.color = '#333'}
      onMouseOut={(e) => e.target.style.color = '#888'}
      title={getTranslation('back', language)}
    >
      ←
    </button>
  );
};

// Breadcrumbs component
const Breadcrumbs = () => {
  const { language } = useLanguage();
  const location = window.location.pathname;
  const segments = location.split('/').filter(seg => seg);
  
  const crumbs = segments.map(seg => {
    const decodedSeg = decodeURIComponent(seg);
    return decodedSeg;
  });
  
  return (
    <div style={{ fontFamily: 'Nunito, Arial, sans-serif', fontWeight: 600, fontSize: window.innerWidth <= 600 ? 18 : 36, color: '#1e3a8a', textAlign: 'center', letterSpacing: 1 }}>
      {crumbs.map((seg, idx) => {
        let displayText;
        if (idx === 0 && seg === 'mecz') {
          displayText = getTranslation('menu', language);
        } else if (seg === 'turniej-rankingowy') {
          displayText = getTranslation('turniej-rankingowy', language);
        } else if (seg === 'dodaj') {
          displayText = getTranslation('dodaj-turniej', language);
        } else if (seg === 'szczegoly') {
          // Check if we came from the add tournament page by looking at URL parameters
          const urlParams = new URLSearchParams(window.location.search);
          if (urlParams.get('from') === 'dodaj') {
            displayText = getTranslation('dodaj-turniej', language);
          } else {
            displayText = getTranslation('szczegoly-turnieju', language);
          }
        } else if (seg === 'mecz') {
          // Skip the intermediate 'mecz' segment
          return null;
        } else if (!isNaN(seg)) {
          // If it's a number (tournament ID), show "rundy"
          displayText = getTranslation('rundy', language);
        } else {
          displayText = getTranslation(seg, language);
        }
        
        return (
          <span key={idx}>
            {idx > 0 && <span style={{ margin: '0 8px' }}>/</span>}
            {displayText}
          </span>
        );
      }).filter(Boolean)}
    </div>
  );
};

const TournamentDetails = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [tournament, setTournament] = useState(null);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [gameToDelete, setGameToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [editingGameId, setEditingGameId] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    roundtype: '',
    playerscore: '',
    opponentscore: '',
    opponent1: '',
    opponent2: '',
    opponent3: '',
    opponent4: ''
  });

  // Fetch tournament details and games
  useEffect(() => {
    const fetchTournamentData = async () => {
      try {
        const token = sessionStorage.getItem('access_token');
        if (!token) {
          throw new Error('Brak tokenu autoryzacji. Zaloguj się ponownie.');
        }
        
        // Fetch tournament details
        const tournamentResponse = await fetch(`/api/tournaments/${tournamentId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!tournamentResponse.ok) {
          throw new Error(`Błąd ${tournamentResponse.status}: ${await tournamentResponse.text()}`);
        }
        
        const currentTournament = await tournamentResponse.json();
        setTournament(currentTournament);
        
        // Fetch games for this tournament
        const gamesResponse = await fetch(`/api/tournaments/${tournamentId}/games`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (!gamesResponse.ok) {
          throw new Error(`Błąd ${gamesResponse.status}: ${await gamesResponse.text()}`);
        }
        
        const gamesData = await gamesResponse.json();
        setGames(gamesData);
        setLoading(false);
        
      } catch (err) {
        console.error('Fetch error:', err);
        setError(`Błąd połączenia: ${err.message}`);
        setLoading(false);
      }
    };

    fetchTournamentData();
  }, [tournamentId]);

  // Refresh data when component becomes visible (e.g., returning from add round page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && tournamentId) {
        // Refetch data when page becomes visible
        const fetchGames = async () => {
          try {
            const token = sessionStorage.getItem('access_token');
            if (token) {
              const gamesResponse = await fetch(`/api/tournaments/${tournamentId}/games`, {
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              });
              
              if (gamesResponse.ok) {
                const gamesData = await gamesResponse.json();
                setGames(gamesData);
              }
            }
          } catch (err) {
            console.error('Refresh error:', err);
          }
        };
        
        fetchGames();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [tournamentId]);

  const getTournamentType = (numberofplayers) => {
    switch (numberofplayers) {
      case 1: return getTranslation('single', language);
      case 2: return getTranslation('dublety', language);
      case 3: return getTranslation('triplety', language);
      case 4: return getTranslation('triplety-z-rezerwowym', language);
      default: return getTranslation('nieznany', language);
    }
  };

  // Helper function to determine which opponent fields to show based on tournament type
  const getOpponentFieldsConfig = (numberofplayers) => {
    switch (numberofplayers) {
      case 1: // Single
        return {
          showOpponent1: true,
          showOpponent2: false,
          showOpponent3: false,
          showOpponent4: false,
          opponent4Label: getTranslation('rezerwowy', language)
        };
      case 2: // Dublety
        return {
          showOpponent1: true,
          showOpponent2: true,
          showOpponent3: false,
          showOpponent4: false,
          opponent4Label: getTranslation('rezerwowy', language)
        };
      case 3: // Triplety
        return {
          showOpponent1: true,
          showOpponent2: true,
          showOpponent3: true,
          showOpponent4: false,
          opponent4Label: getTranslation('rezerwowy', language)
        };
      case 4: // Triplety z rezerwowym
        return {
          showOpponent1: true,
          showOpponent2: true,
          showOpponent3: true,
          showOpponent4: true,
          opponent4Label: getTranslation('rezerwowy', language)
        };
      default:
        return {
          showOpponent1: false,
          showOpponent2: false,
          showOpponent3: false,
          showOpponent4: false,
          opponent4Label: getTranslation('rezerwowy', language)
        };
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL');
  };

  const handleEditRound = (game) => {
    setEditingGameId(game.game_id);
    setEditFormData({
      roundtype: game.roundtype || '',
      playerscore: game.playerscore || '',
      opponentscore: game.opponentscore || '',
      opponent1: game.opponent1 || '',
      opponent2: game.opponent2 || '',
      opponent3: game.opponent3 || '',
      opponent4: game.opponent4 || ''
    });
  };

  const handleDeleteRound = (game) => {
    setGameToDelete(game);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!gameToDelete) return;
    
    setDeleting(true);
    try {
      const token = sessionStorage.getItem('access_token');
      
      const response = await fetch(`/api/tournaments/games/${gameToDelete.game_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Błąd ${response.status}: ${errorText}`);
      }
      
      // Refresh the games list to show renumbered rounds
      const gamesResponse = await fetch(`/api/tournaments/${tournamentId}/games`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (gamesResponse.ok) {
        const updatedGames = await gamesResponse.json();
        setGames(updatedGames);
      } else {
        // Fallback: remove the game from local state if refresh fails
        setGames(prevGames => prevGames.filter(game => game.game_id !== gameToDelete.game_id));
      }
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setGameToDelete(null);
      
    } catch (err) {
      console.error('Delete error:', err);
      setError(`Błąd podczas usuwania rundy: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setGameToDelete(null);
  };

  const handleEditInputChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditSubmit = async () => {
    if (!editingGameId) return;
    
    // Validation
    if (!editFormData.roundtype) {
      setError(getTranslation('wybierz-typ-rundy', language));
      return;
    }
    
    if (!editFormData.playerscore || !editFormData.opponentscore) {
      setError(getTranslation('wprowadz-wyniki-dla-obu-druzyn', language));
      return;
    }
    
    const playerScore = parseInt(editFormData.playerscore);
    const opponentScore = parseInt(editFormData.opponentscore);
    
    if (isNaN(playerScore) || isNaN(opponentScore)) {
      setError(getTranslation('wyniki-musza-byc-liczbami', language));
      return;
    }
    
    if (playerScore < 0 || playerScore > 13 || opponentScore < 0 || opponentScore > 13) {
      setError(getTranslation('wyniki-musza-byc-miedzy-0-a-13', language));
      return;
    }
    
    if (playerScore === opponentScore) {
      setError(getTranslation('petanque-nie-ma-remisow', language));
      return;
    }
    
    setEditing(true);
    setError(null);
    
    try {
      const token = sessionStorage.getItem('access_token');
      
      const updateData = {
        roundtype: editFormData.roundtype,
        playerscore: playerScore,
        opponentscore: opponentScore,
        opponent1: editFormData.opponent1.trim() || null,
        opponent2: editFormData.opponent2.trim() || null,
        opponent3: editFormData.opponent3.trim() || null,
        opponent4: editFormData.opponent4.trim() || null
      };
      
      const response = await fetch(`/api/tournaments/games/${editingGameId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Błąd ${response.status}: ${errorText}`);
      }
      
      // Update the game in the local state
      setGames(prevGames => 
        prevGames.map(game => 
          game.game_id === editingGameId 
            ? { ...game, ...updateData }
            : game
        )
      );
      
      // Exit edit mode and reset state
      setEditingGameId(null);
      
    } catch (err) {
      console.error('Edit error:', err);
      setError(`Błąd podczas edytowania rundy: ${err.message}`);
    } finally {
      setEditing(false);
    }
  };

  const handleEditCancel = () => {
    setEditingGameId(null);
    setEditFormData({
      roundtype: '',
      playerscore: '',
      opponentscore: '',
      opponent1: '',
      opponent2: '',
      opponent3: '',
      opponent4: ''
    });
  };

  const getRoundTypeDisplay = (roundtype) => {
    switch (roundtype) {
      case 'puchar': return 'P';
      case 'buchholz': return 'B';
      case 'grupa': return 'G';
      case 'P': return 'P';
      case 'B': return 'B';
      case 'G': return 'G';
      default: return roundtype || '-';
    }
  };

  if (loading) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          ShootMe
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Ładowanie danych...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
        <h1 style={{
          fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
          fontWeight: 'bold',
          color: '#7c3aed',
          margin: '0 0 16px 0',
          textAlign: 'center',
          fontFamily: 'Nunito, Arial, sans-serif'
        }}>
          ShootMe
        </h1>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '40px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <h2 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.5rem'
            }}>
              Błąd ładowania turnieju
            </h2>
            <p style={{ color: 'red', fontFamily: 'Nunito, Arial, sans-serif', marginBottom: '20px' }}>
              {error}
            </p>
            <button 
              onClick={() => window.location.reload()} 
              style={{
                background: '#7c3aed',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontFamily: 'Nunito, Arial, sans-serif',
                fontSize: '14px'
              }}
            >
              Spróbuj ponownie
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: '100vh', padding: 20, background: '#f0f8ff' }}>
      {/* Language Selector */}
      <div style={{
        position: 'absolute',
        top: '20px',
        right: '20px',
        zIndex: 1000
      }}>
        <LanguageSelector />
      </div>

      {/* Main Title */}
      <h1 style={{
        fontSize: window.innerWidth <= 600 ? '2.5rem' : '3.5rem',
        fontWeight: 'bold',
        color: '#7c3aed',
        margin: '0 0 16px 0',
        textAlign: 'center',
        fontFamily: 'Nunito, Arial, sans-serif'
      }}>
        {getTranslation('shootMe', language)}
      </h1>
      
      {/* Back button and breadcrumbs in horizontal layout */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        gap: '20px',
        margin: '24px 0'
      }}>
        <BackButton tournamentId={tournamentId} />
        <Breadcrumbs />
      </div>

      {/* Tournament Info */}
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          marginBottom: '20px'
        }}>
          <h2 style={{
            color: '#1e3a8a',
            fontFamily: 'Nunito, Arial, sans-serif',
            marginBottom: '20px',
            fontSize: '1.5rem',
            textAlign: 'center'
          }}>
            {tournament.tournamentname}
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: window.innerWidth <= 600 ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '15px',
            fontFamily: 'Nunito, Arial, sans-serif',
            fontSize: '14px'
          }}>
            <div><strong>{getTranslation('data', language)}:</strong> {formatDate(tournament.tournamentdate)}</div>
            <div><strong>{getTranslation('miasto', language)}:</strong> {tournament.city || '-'}</div>
            <div><strong>{getTranslation('typ', language)}:</strong> {getTournamentType(tournament.numberofplayers)}</div>
          </div>
        </div>

        {/* Games List */}
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{
            color: '#1e3a8a',
            fontFamily: 'Nunito, Arial, sans-serif',
            marginBottom: '20px',
            fontSize: '1.3rem',
            textAlign: 'center'
          }}>
            {getTranslation('rundy-turnieju', language)}
          </h3>
          
          {games.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ 
                fontFamily: 'Nunito, Arial, sans-serif', 
                fontSize: '1.2rem', 
                color: '#666',
                marginBottom: '20px'
              }}>
                {getTranslation('dodaj-pierwsza-runde', language)}
              </p>
              <button
                onClick={() => {
                  const urlParams = new URLSearchParams(window.location.search);
                  const fromParam = urlParams.get('from');
                  let url = `/mecz/turniej-rankingowy/szczegoly/${tournamentId}/dodaj-runde`;
                  if (fromParam) {
                    url += `?from=${fromParam}`;
                  }
                  navigate(url);
                }}
                style={{
                  background: '#7c3aed',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => e.target.style.background = '#6d28d9'}
                onMouseOut={(e) => e.target.style.background = '#7c3aed'}
              >
                {getTranslation('dodaj-runde', language)}
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                fontFamily: 'Nunito, Arial, sans-serif'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('runda', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('puchar-buchholz-grupa', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('punkty-zdobyte', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('punkty-stracone', language)}</th>
                    {(() => {
                      const config = getOpponentFieldsConfig(tournament.numberofplayers);
                      return (
                        <>
                          {config.showOpponent1 && (
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('przeciwnik1', language)}</th>
                          )}
                          {config.showOpponent2 && (
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('przeciwnik2', language)}</th>
                          )}
                          {config.showOpponent3 && (
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{getTranslation('przeciwnik3', language)}</th>
                          )}
                          {config.showOpponent4 && (
                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>{config.opponent4Label}</th>
                          )}
                        </>
                      );
                    })()}
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>{getTranslation('edytuj', language)}</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>{getTranslation('usun', language)}</th>
                  </tr>
                </thead>
                <tbody>
                  {games.map((game) => {
                    const isEditing = editingGameId === game.game_id;
                    
                    // Determine row color based on score comparison
                    const playerScore = parseInt(game.playerscore) || 0;
                    const opponentScore = parseInt(game.opponentscore) || 0;
                    
                    let rowStyle = { borderBottom: '1px solid #dee2e6' };
                    
                    if (playerScore > opponentScore) {
                      // Win - green background
                      rowStyle.backgroundColor = '#f0fdf4';
                      rowStyle.borderLeft = '4px solid #22c55e';
                    } else if (playerScore < opponentScore) {
                      // Loss - red background
                      rowStyle.backgroundColor = '#fef2f2';
                      rowStyle.borderLeft = '4px solid #ef4444';
                    }
                    
                    if (isEditing) {
                      rowStyle.backgroundColor = '#fef3c7';
                      rowStyle.borderLeft = '4px solid #f59e0b';
                    }
                    
                    return (
                      <tr key={game.game_id} style={rowStyle}>
                        <td style={{ padding: '12px' }}>{game.round || '-'}</td>
                        
                        {/* Round Type */}
                        <td style={{ padding: '12px' }}>
                          {isEditing ? (
                            <select
                              value={editFormData.roundtype}
                              onChange={(e) => handleEditInputChange('roundtype', e.target.value)}
                              style={{
                                padding: '6px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontFamily: 'Nunito, Arial, sans-serif',
                                fontSize: '12px',
                                width: '100%'
                              }}
                            >
                              <option value="">{getTranslation('wybierz-typ', language)}</option>
                              <option value="B">{getTranslation('runda-buchholza', language)}</option>
                              <option value="P">{getTranslation('runda-pucharowa', language)}</option>
                              <option value="G">{getTranslation('runda-grupowa', language)}</option>
                            </select>
                          ) : (
                            getRoundTypeDisplay(game.roundtype)
                          )}
                        </td>
                        
                        {/* Player Score */}
                        <td style={{ padding: '12px' }}>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editFormData.playerscore}
                              onChange={(e) => handleEditInputChange('playerscore', e.target.value)}
                              min="0"
                              max="13"
                              style={{
                                padding: '6px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontFamily: 'Nunito, Arial, sans-serif',
                                fontSize: '12px',
                                width: '60px'
                              }}
                            />
                          ) : (
                            game.playerscore || '-'
                          )}
                        </td>
                        
                        {/* Opponent Score */}
                        <td style={{ padding: '12px' }}>
                          {isEditing ? (
                            <input
                              type="number"
                              value={editFormData.opponentscore}
                              onChange={(e) => handleEditInputChange('opponentscore', e.target.value)}
                              min="0"
                              max="13"
                              style={{
                                padding: '6px',
                                border: '1px solid #d1d5db',
                                borderRadius: '4px',
                                fontFamily: 'Nunito, Arial, sans-serif',
                                fontSize: '12px',
                                width: '60px'
                              }}
                            />
                          ) : (
                            game.opponentscore || '-'
                          )}
                        </td>
                        
                        {/* Opponent Fields */}
                        {(() => {
                          const config = getOpponentFieldsConfig(tournament.numberofplayers);
                          return (
                            <>
                              {config.showOpponent1 && (
                                <td style={{ padding: '12px' }}>
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={editFormData.opponent1}
                                      onChange={(e) => handleEditInputChange('opponent1', e.target.value)}
                                      maxLength={30}
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        fontFamily: 'Nunito, Arial, sans-serif',
                                        fontSize: '12px',
                                        width: '100%'
                                      }}
                                    />
                                  ) : (
                                    game.opponent1 || '-'
                                  )}
                                </td>
                              )}
                              {config.showOpponent2 && (
                                <td style={{ padding: '12px' }}>
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={editFormData.opponent2}
                                      onChange={(e) => handleEditInputChange('opponent2', e.target.value)}
                                      maxLength={30}
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        fontFamily: 'Nunito, Arial, sans-serif',
                                        fontSize: '12px',
                                        width: '100%'
                                      }}
                                    />
                                  ) : (
                                    game.opponent2 || '-'
                                  )}
                                </td>
                              )}
                              {config.showOpponent3 && (
                                <td style={{ padding: '12px' }}>
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={editFormData.opponent3}
                                      onChange={(e) => handleEditInputChange('opponent3', e.target.value)}
                                      maxLength={30}
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        fontFamily: 'Nunito, Arial, sans-serif',
                                        fontSize: '12px',
                                        width: '100%'
                                      }}
                                    />
                                  ) : (
                                    game.opponent3 || '-'
                                  )}
                                </td>
                              )}
                              {config.showOpponent4 && (
                                <td style={{ padding: '12px' }}>
                                  {isEditing ? (
                                    <input
                                      type="text"
                                      value={editFormData.opponent4}
                                      onChange={(e) => handleEditInputChange('opponent4', e.target.value)}
                                      maxLength={30}
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '4px',
                                        fontFamily: 'Nunito, Arial, sans-serif',
                                        fontSize: '12px',
                                        width: '100%'
                                      }}
                                    />
                                  ) : (
                                    game.opponent4 || '-'
                                  )}
                                </td>
                              )}
                            </>
                          );
                        })()}
                        
                        {/* Action Buttons */}
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {isEditing ? (
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleEditSubmit();
                                }}
                                disabled={editing}
                                style={{
                                  background: editing ? '#9ca3af' : '#22c55e',
                                  color: 'white',
                                  border: 'none',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  cursor: editing ? 'not-allowed' : 'pointer',
                                  fontFamily: 'Nunito, Arial, sans-serif',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                {editing ? '...' : '✓'}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleEditCancel();
                                }}
                                disabled={editing}
                                style={{
                                  background: '#6b7280',
                                  color: 'white',
                                  border: 'none',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  cursor: editing ? 'not-allowed' : 'pointer',
                                  fontFamily: 'Nunito, Arial, sans-serif',
                                  fontSize: '10px',
                                  fontWeight: '600'
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEditRound(game)}
                              style={{
                                background: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontFamily: 'Nunito, Arial, sans-serif',
                                fontSize: '12px',
                                fontWeight: '600',
                                transition: 'background 0.2s'
                              }}
                              onMouseOver={(e) => e.target.style.background = '#2563eb'}
                              onMouseOut={(e) => e.target.style.background = '#3b82f6'}
                            >
                              {getTranslation('edytuj', language)}
                            </button>
                          )}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <button
                            onClick={() => handleDeleteRound(game)}
                            disabled={isEditing}
                            style={{
                              background: isEditing ? '#9ca3af' : '#ef4444',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: isEditing ? 'not-allowed' : 'pointer',
                              fontFamily: 'Nunito, Arial, sans-serif',
                              fontSize: '12px',
                              fontWeight: '600',
                              transition: 'background 0.2s'
                            }}
                            onMouseOver={(e) => !isEditing && (e.target.style.background = '#dc2626')}
                            onMouseOut={(e) => !isEditing && (e.target.style.background = '#ef4444')}
                          >
                            {getTranslation('usun', language)}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Add Round Button and Finish Tournament Button - Always visible below the table */}
          {games.length > 0 && (
            <div style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
                <button
                  onClick={() => {
                    const urlParams = new URLSearchParams(window.location.search);
                    const fromParam = urlParams.get('from');
                    let url = `/mecz/turniej-rankingowy/szczegoly/${tournamentId}/dodaj-runde`;
                    if (fromParam) {
                      url += `?from=${fromParam}`;
                    }
                    navigate(url);
                  }}
                  style={{
                    background: '#7c3aed',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#6d28d9'}
                  onMouseOut={(e) => e.target.style.background = '#7c3aed'}
                >
                  {getTranslation('dodaj-runde', language)}
                </button>
                <button
                  onClick={() => {
                    const urlParams = new URLSearchParams(window.location.search);
                    const fromParam = urlParams.get('from');
                    let url = `/mecz/turniej-rankingowy/szczegoly/${tournamentId}/zakoncz`;
                    if (fromParam) {
                      url += `?from=${fromParam}`;
                    }
                    navigate(url);
                  }}
                  style={{
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => e.target.style.background = '#b91c1c'}
                  onMouseOut={(e) => e.target.style.background = '#dc2626'}
                >
                  {getTranslation('zakoncz-turniej', language)}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '400px',
            width: '90%',
            textAlign: 'center',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.3rem'
            }}>
              Potwierdzenie usunięcia
            </h3>
            <p style={{
              color: '#666',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '30px',
              fontSize: '16px',
              lineHeight: '1.5'
            }}>
              Czy na pewno chcesz usunąć rundę {gameToDelete?.round}?<br />
              Ta operacja jest nieodwracalna.
            </p>
            <div style={{
              display: 'flex',
              gap: '15px',
              justifyContent: 'center'
            }}>
              <button
                onClick={handleDeleteCancel}
                disabled={deleting}
                style={{
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'background 0.2s',
                  opacity: deleting ? 0.6 : 1
                }}
                onMouseOver={(e) => !deleting && (e.target.style.background = '#4b5563')}
                onMouseOut={(e) => !deleting && (e.target.style.background = '#6b7280')}
              >
                {getTranslation('nie', language)}
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleting}
                style={{
                  background: deleting ? '#9ca3af' : '#ef4444',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontSize: '16px',
                  fontWeight: '600',
                  transition: 'background 0.2s'
                }}
                onMouseOver={(e) => !deleting && (e.target.style.background = '#dc2626')}
                onMouseOut={(e) => !deleting && (e.target.style.background = '#ef4444')}
              >
                {deleting ? getTranslation('usuwanie', language) : getTranslation('tak', language)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {false && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{
              color: '#1e3a8a',
              fontFamily: 'Nunito, Arial, sans-serif',
              marginBottom: '20px',
              fontSize: '1.3rem',
              textAlign: 'center'
            }}>
              {getTranslation('edytuj-runde', language)}
            </h3>
            
            {error && (
              <div style={{
                background: '#fee2e2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '12px',
                borderRadius: '6px',
                marginBottom: '20px',
                fontFamily: 'Nunito, Arial, sans-serif',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
            
            <form onSubmit={(e) => { e.preventDefault(); handleEditSubmit(); }}>
              {/* Round Type */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontWeight: '600',
                  color: '#1e3a8a'
                }}>
                  {getTranslation('typ-rundy', language)}:
                </label>
                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', fontFamily: 'Nunito, Arial, sans-serif', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="editRoundtype"
                      value="B"
                      checked={editFormData.roundtype === 'B'}
                      onChange={(e) => handleEditInputChange('roundtype', e.target.value)}
                      style={{ marginRight: '8px' }}
                    />
                    {getTranslation('runda-buchholza', language)}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', fontFamily: 'Nunito, Arial, sans-serif', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="editRoundtype"
                      value="P"
                      checked={editFormData.roundtype === 'P'}
                      onChange={(e) => handleEditInputChange('roundtype', e.target.value)}
                      style={{ marginRight: '8px' }}
                    />
                    {getTranslation('runda-pucharowa', language)}
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', fontFamily: 'Nunito, Arial, sans-serif', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="editRoundtype"
                      value="G"
                      checked={editFormData.roundtype === 'G'}
                      onChange={(e) => handleEditInputChange('roundtype', e.target.value)}
                      style={{ marginRight: '8px' }}
                    />
                    {getTranslation('runda-grupowa', language)}
                  </label>
                </div>
              </div>

              {/* Opponent Fields */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontWeight: '600',
                  color: '#1e3a8a'
                }}>
                  Przeciwnicy:
                </label>
                <div style={{ display: 'grid', gap: '12px' }}>
                  {tournament.numberofplayers >= 1 && (
                    <input
                      type="text"
                      value={editFormData.opponent1}
                      onChange={(e) => handleEditInputChange('opponent1', e.target.value)}
                      placeholder={getTranslation('przeciwnik1', language)}
                      maxLength={30}
                      style={{
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontFamily: 'Nunito, Arial, sans-serif',
                        fontSize: '14px'
                      }}
                    />
                  )}
                  {tournament.numberofplayers >= 2 && (
                    <input
                      type="text"
                      value={editFormData.opponent2}
                      onChange={(e) => handleEditInputChange('opponent2', e.target.value)}
                      placeholder={getTranslation('przeciwnik2', language)}
                      maxLength={30}
                      style={{
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontFamily: 'Nunito, Arial, sans-serif',
                        fontSize: '14px'
                      }}
                    />
                  )}
                  {tournament.numberofplayers >= 3 && (
                    <input
                      type="text"
                      value={editFormData.opponent3}
                      onChange={(e) => handleEditInputChange('opponent3', e.target.value)}
                      placeholder={getTranslation('przeciwnik3', language)}
                      maxLength={30}
                      style={{
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontFamily: 'Nunito, Arial, sans-serif',
                        fontSize: '14px'
                      }}
                    />
                  )}
                  {tournament.numberofplayers >= 4 && (
                    <input
                      type="text"
                      value={editFormData.opponent4}
                      onChange={(e) => handleEditInputChange('opponent4', e.target.value)}
                      placeholder={getTranslation('rezerwowy', language)}
                      maxLength={30}
                      style={{
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontFamily: 'Nunito, Arial, sans-serif',
                        fontSize: '14px'
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Score Fields */}
              <div style={{ marginBottom: '30px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontFamily: 'Nunito, Arial, sans-serif',
                  fontWeight: '600',
                  color: '#1e3a8a'
                }}>
                  {getTranslation('wynik', language)}:
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '4px',
                      fontFamily: 'Nunito, Arial, sans-serif',
                      fontSize: '12px',
                      color: '#6b7280'
                    }}>
                      Moja drużyna
                    </label>
                    <input
                      type="number"
                      value={editFormData.playerscore}
                      onChange={(e) => handleEditInputChange('playerscore', e.target.value)}
                      min="0"
                      max="13"
                      title="Wynik musi być między 0 a 13"
                      style={{
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontFamily: 'Nunito, Arial, sans-serif',
                        fontSize: '14px',
                        width: '100%'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{
                      display: 'block',
                      marginBottom: '4px',
                      fontFamily: 'Nunito, Arial, sans-serif',
                      fontSize: '12px',
                      color: '#6b7280'
                    }}>
                      Przeciwnicy
                    </label>
                    <input
                      type="number"
                      value={editFormData.opponentscore}
                      onChange={(e) => handleEditInputChange('opponentscore', e.target.value)}
                      min="0"
                      max="13"
                      title="Wynik musi być między 0 a 13"
                      style={{
                        padding: '10px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontFamily: 'Nunito, Arial, sans-serif',
                        fontSize: '14px',
                        width: '100%'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '15px',
                justifyContent: 'center'
              }}>
                <button
                  type="button"
                  onClick={handleEditCancel}
                  disabled={editing}
                  style={{
                    background: '#6b7280',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: editing ? 'not-allowed' : 'pointer',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'background 0.2s',
                    opacity: editing ? 0.6 : 1
                  }}
                  onMouseOver={(e) => !editing && (e.target.style.background = '#4b5563')}
                  onMouseOut={(e) => !editing && (e.target.style.background = '#6b7280')}
                >
                  {getTranslation('anuluj', language)}
                </button>
                <button
                  type="submit"
                  disabled={editing}
                  style={{
                    background: editing ? '#9ca3af' : '#7c3aed',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: editing ? 'not-allowed' : 'pointer',
                    fontFamily: 'Nunito, Arial, sans-serif',
                    fontSize: '16px',
                    fontWeight: '600',
                    transition: 'background 0.2s'
                  }}
                  onMouseOver={(e) => !editing && (e.target.style.background = '#6d28d9')}
                  onMouseOut={(e) => !editing && (e.target.style.background = '#7c3aed')}
                >
                  {editing ? getTranslation('zapisywanie', language) : getTranslation('zapisz-zmiany', language)}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TournamentDetails;
